/**
 * <s-quick-add> — one-tap add-to-cart for product cards.
 *
 * Reads data-variant-id from the host and adds one unit when its inner <button> is clicked,
 * using Shopify Standard Actions (updateCart + openCart) so the cart drawer opens on-page.
 * Falls back to the native /cart/add route when Standard Actions are unavailable.
 * Lives inside filterable grids, so it uses no <form> (the grid sits inside the filter form).
 */
class SQuickAdd extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #onClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.#busy) return;

    const variantId = String(this.dataset.variantId || '').trim();
    if (!variantId) return;

    if (!window.Shopify?.actions?.updateCart) {
      const url = new URL(this.dataset.cartAddUrl || '/cart/add', window.location.origin);
      url.searchParams.set('id', variantId);
      url.searchParams.set('quantity', '1');
      window.location.assign(url.toString());
      return;
    }

    this.#setBusy(true, button);
    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'collection', detail: { source: 's-quick-add' } } }
      );
      if (result?.userErrors?.length) {
        button.setAttribute('title', result.userErrors[0].message || '');
      } else {
        await window.Shopify.actions.openCart?.();
      }
    } catch (_error) {
      button.setAttribute('title', this.dataset.errorMessage || '');
    } finally {
      this.#setBusy(false, button);
    }
  }

  #setBusy(busy, button) {
    this.#busy = busy;
    button.setAttribute('aria-disabled', busy ? 'true' : 'false');
    button.classList.toggle('opacity-60', busy);
    button.classList.toggle('cursor-wait', busy);
  }
}

if (!customElements.get('s-quick-add')) {
  customElements.define('s-quick-add', SQuickAdd);
}
